import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  CheckCircle,
  Clock,
  AlertCircle,
  UploadIcon,
  TrashIcon,
  FileText,
  Mic,
} from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "../../components/ui/card";
import { useDropzone } from "react-dropzone";
import { useDispatch } from "react-redux";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import MagicLinkForm from "../../components/claimant/magic-link-form";
import {
  GetClaimDetailApi,
  CreateMinimalClaimApi,
  UpdateClaimApi,
} from "../Claims/ClaimsAPI";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "../../components/ui/dialog";
import ChatInterface from "../../components/claimant/chat-interface";
import ClaimPlanDetails from "../../components/claimant/ClaimPlanDetails";
import logo from "../../logo_comerco.png";
function CreateTicketFromMail() {
  const { claimId: urlPlanId } = useParams();
  const [currentStep, setCurrentStep] = useState("plan-verification");
  const [planDetails, setPlanDetails] = useState(null);
  const [claimDetails, setClaimDetails] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [demoProgress, setDemoProgress] = useState(1);
  const [verifiedPlan, setVerifiedPlan] = useState("");
  const [files, setFiles] = useState([]);
  const [uploadedMedia, setUploadedMedia] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const dispatch = useDispatch();

  const demoSteps = [
    {
      id: 1,
      title: "Plan Details",
      description:
        "View your plan ID, selected issue type, and issue description",
      status:
        currentStep === "plan-verification"
          ? "current"
          : planDetails || claimDetails
          ? "completed"
          : "pending",
    },
    {
      id: 2,
      title: "Troubleshoot & Evidence Upload",
      description: "Upload photos, documents, or record audio evidence",
      status:
        currentStep === "evidence-upload"
          ? "current"
          : currentStep === "claim-submission"
          ? "completed"
          : "pending",
    },
    {
      id: 3,
      title: "Claim Submission",
      description: "Review and submit your claim for processing",
      status: submissionResult
        ? "completed"
        : currentStep === "claim-submission"
        ? "current"
        : "pending",
    },
  ];

  const handlePlanVerified = (verifiedPlan) => {
    setPlanDetails(verifiedPlan);
    setCurrentStep("evidence-upload");
    setDemoProgress(2);
  };

  const handleSubmitClaim = async () => {
    setIsSubmitting(true);
    try {
      const payload = {};
      let response;
      if (urlPlanId) {
        payload.claimId = claimDetails?.claimID;
        payload.status = "Submitted";
        payload.description = claimDetails?.description;
        payload.issueType = claimDetails?.issueType;
        payload.claimAmount = 0;
        response = await dispatch(UpdateClaimApi(payload));
      } else {
        payload.planID = planDetails.planID;
        response = await dispatch(CreateMinimalClaimApi(payload));
      }
      if (response.success) {
        setSubmissionResult(response.data);
        setCurrentStep("claim-submission");
        setDemoProgress(3);
        setTimeout(() => {
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: "smooth",
          });
        }, 100); // delay allows time for UI to update
      } else {
        console.error("Error submitting claim:", response.error);
      }
    } catch (error) {
      console.error("Error submitting claim:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const mappedFiles = acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );
    setFiles((prev) => [...prev, ...mappedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [],
      "application/pdf": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [],
      "video/*": [],
    },
    multiple: true,
  });

  const removeFile = (name) => {
    setFiles((prev) => prev.filter((file) => file.name !== name));
  };

  useEffect(() => {
    if (urlPlanId) {
      const verifyPlanFromUrl = async () => {
        try {
          const response = await GetClaimDetailApi(urlPlanId.toString());
          if (response.success && response.payload) {
            setClaimDetails(response.payload);
            // setCurrentStep("evidence-upload");
            // setDemoProgress(2);
          } else {
            console.error("Failed to verify plan from URL:", response.error);
          }
        } catch (error) {
          console.error("Error verifying plan from URL:", error);
        }
      };

      verifyPlanFromUrl();
    }
  }, [urlPlanId, currentStep, planDetails]);

  const getStepIcon = (step) => {
    switch (step.status) {
      case "completed":
        return <CheckCircle className="text-green-500" size={20} />;
      case "current":
        return <Clock className="text-blue-500" size={20} />;
      default:
        return <AlertCircle className="text-slate-400" size={20} />;
    }
  };
  const handleFilesFromChat = (newFiles) => {
    setUploadedMedia((prev) => {
      const newUniqueFiles = [];

      newFiles.forEach((file) => {
        const alreadyExists = prev.some((f) => f.id === file.id);
        if (alreadyExists) return;

        const isPreviewable =
          file.type?.startsWith("image/") || file.type?.startsWith("video/");

        const fileObject = file.file || file.fileObject || null;

        newUniqueFiles.push({
          ...file,
          fileObject, // ✅ ensure this is saved
          previewUrl:
            isPreviewable && fileObject
              ? URL.createObjectURL(fileObject)
              : file.previewUrl || null,
        });
      });

      return [...prev, ...newUniqueFiles];
    });
  };

  useEffect(() => {
    const currentMedia = uploadedMedia;
    return () => {
      currentMedia.forEach((file) => {
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
    };
  }, [uploadedMedia]);
  useEffect(() => {
    const generateSessionId = () => {
      const now = new Date();
      const formattedDate = now
        .toISOString()
        .replace(/[-:.TZ]/g, "")
        .slice(0, 14);
      return `${formattedDate}`;
    };

    const newSessionId = generateSessionId();
    setSessionId(newSessionId);
  }, []);

  return (
    <>
      <div className="max-w-6xl mx-auto !p-4 !space-y-6">
        <img src={logo} style={{ width: 120 }} />
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="!text-2xl">Complete Claim</CardTitle>
                <p className="text-slate-600 mt-2">
                  Experience the full step 1-3 claim process
                </p>
              </div>
              <div className="gap-2 text-sm text-slate-700">
                <div>
                  <p className="text-slate-500">
                    Plan ID: <b>{claimDetails?.plan?.businessPlanID}</b>
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">
                    Product Name: <b>{claimDetails?.plan?.productName}</b>
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">
                    Customer: <b>{claimDetails?.customer?.fullName}</b>
                  </p>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-3 gap-3 max-w-5xl mx-auto">
              {demoSteps.map((step) => (
                <div
                  key={step.id}
                  className={`p-3 rounded-lg border-2 transition-all min-h-[110px] flex flex-col ${
                    step.status === "current"
                      ? "border-blue-500 bg-blue-50"
                      : step.status === "completed"
                      ? "border-green-500 bg-green-50"
                      : "border-slate-200 bg-slate-50"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-shrink-0">{getStepIcon(step)}</div>
                    <Badge
                      variant={
                        step.status === "completed" ? "default" : "outline"
                      }
                      className="text-xs flex-shrink-0"
                    >
                      Step {step.id}
                    </Badge>
                  </div>
                  <h3 className="font-semibold text-sm leading-tight mb-1">
                    {step.title}
                  </h3>
                  <p className="text-xs text-slate-600 leading-relaxed flex-1">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="max-w-5xl mx-auto">
          {currentStep === "plan-verification" && (
            <Card>
              <CardHeader>
                <CardTitle>Step 1: Plan Details</CardTitle>
                <p className="text-slate-600">
                  Check plan ID, selected issue type, and issue description to
                  move further
                </p>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <ClaimPlanDetails />
                </div>

                <div className="flex justify-end">
                  <Button onClick={() => setCurrentStep("evidence-upload")}>
                    Continue to Evidence Upload →
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {currentStep === "evidence-upload" && (
            <Card>
              <CardHeader className="bg-slate-50 rounded-lg">
                <ClaimPlanDetails currentStep={currentStep} />
              </CardHeader>
              <CardHeader>
                <CardTitle>Step 2: Troubleshoot & Evidence Upload</CardTitle>
                <p className="text-slate-600">
                  Add supporting evidence for your claim (optional for demo)
                </p>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <ChatInterface
                    key={verifiedPlan}
                    claim={claimDetails}
                    onFilesUpload={handleFilesFromChat}
                    uploadedFiles={uploadedFiles}
                    setUploadedFiles={setUploadedFiles}
                  />
                </div>

                <div className="flex justify-between mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep("plan-verification")}
                  >
                    ← Back
                  </Button>
                  <Button onClick={() => setCurrentStep("claim-submission")}>
                    Continue to Submission →
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {currentStep === "claim-submission" && (
            <Card>
              <CardHeader>
                <CardTitle>Step 3: Review & Submit</CardTitle>
                <p className="text-slate-600">
                  Review your claim details and submit
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 mb-6">
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold mb-2">Claim Summary</h4>
                    <div className="text-sm grid grid-cols-12 gap-y-2 gap-x-2">
                      <div className="col-span-3 text-slate-500">Plan ID:</div>
                      <div className="col-span-9 font-medium">
                        {claimDetails?.plan?.businessPlanID}
                      </div>

                      <div className="col-span-3 text-slate-500">Product:</div>
                      <div className="col-span-9 font-medium">
                        {claimDetails?.plan?.productName}
                      </div>

                      <div className="col-span-3 text-slate-500">
                        Issue Type:
                      </div>
                      <div className="col-span-9 font-medium">
                        {claimDetails?.issueType}
                      </div>

                      <div className="col-span-3 text-slate-500">
                        Issue Description:
                      </div>
                      <div className="col-span-9 font-medium text-wrap break-words">
                        {claimDetails?.description ||
                          "No description available."}
                      </div>
                    </div>
                  </div>
                  {uploadedMedia.length > 0 && (
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold mb-2">Uploaded Evidence</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {uploadedMedia.map((file) => {
                          // Recreate preview URL if needed
                          let previewUrl = file.previewUrl;

                          if (
                            !previewUrl &&
                            file.fileObject &&
                            (file.type.startsWith("image/") ||
                              file.type.startsWith("video/"))
                          ) {
                            previewUrl = URL.createObjectURL(file.fileObject);
                            file.previewUrl = previewUrl; // ⚠️ mutate once so it’s cached
                          }

                          return (
                            <div key={file.id} className="relative">
                              {file.type.startsWith("image/") && previewUrl ? (
                                <div className="aspect-square overflow-hidden rounded-lg border">
                                  <img
                                    src={previewUrl}
                                    alt={file.name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              ) : file.type.startsWith("video/") &&
                                previewUrl ? (
                                <div className="aspect-square bg-gray-100 rounded-lg border flex items-center justify-center">
                                  <video
                                    className="w-full h-full object-cover"
                                    controls
                                  >
                                    <source src={previewUrl} type={file.type} />
                                  </video>
                                </div>
                              ) : file.type.startsWith("audio/") ? (
                                <div className="aspect-square bg-gray-100 rounded-lg border flex items-center justify-center">
                                  <Mic className="text-gray-400" size={24} />
                                </div>
                              ) : (
                                <div className="aspect-square bg-gray-100 rounded-lg border flex items-center justify-center">
                                  <FileText
                                    className="text-gray-400"
                                    size={24}
                                  />
                                </div>
                              )}
                              <p className="text-xs mt-1 truncate">
                                {file.name}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {!submissionResult ? (
                    <div className="flex justify-between mt-6">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentStep("evidence-upload")}
                      >
                        ← Back
                      </Button>
                      <Button
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => setShowSubmitConfirm(true)}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Submitting..." : "Submit Claim"}
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center p-6 bg-green-50 border border-green-200 rounded-lg">
                      <CheckCircle
                        className="text-green-500 mx-auto mb-3"
                        size={48}
                      />
                      <h3 className="text-lg font-semibold text-green-900 mb-2">
                        Claim Submitted Successfully!
                      </h3>
                      <p className="text-green-700 mb-4">
                        Your claim {submissionResult.businessClaimID} has been
                        submitted and is under review.
                      </p>
                      {/* <Button variant="outline" onClick={resetDemo}>
                        Start New Claim
                      </Button> */}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        <Dialog open={showSubmitConfirm} onOpenChange={setShowSubmitConfirm}>
          <DialogContent className="max-w-md">
            <h2 className="text-lg font-semibold mb-2">Confirm Submission</h2>
            <p className="text-sm text-slate-700 mb-4">
              Please review and confirm that all the details are correct before
              submitting your claim.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowSubmitConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => {
                  setShowSubmitConfirm(false);
                  handleSubmitClaim();
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}

export default CreateTicketFromMail;
